import React, { useState } from 'react';
import {
  Box, Button, Container, Paper, TextField, Typography,
  Link, Alert, InputAdornment, IconButton, Stack
} from '@mui/material';
import LockResetIcon from '@mui/icons-material/LockReset';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useMutation } from '@apollo/client';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { RESET_PASSWORD_MUTATION } from '../graphql/queries';

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [form, setForm] = useState({ password: '', confirm: '' });
  const [showPwd, setShowPwd] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const [resetPassword, { loading }] = useMutation(RESET_PASSWORD_MUTATION);

  const handleChange = (e) => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.password.length < 8) { setError('Password must be at least 8 characters'); return; }
    if (form.password !== form.confirm) { setError('Passwords do not match'); return; }
    try {
      await resetPassword({ variables: { token, newPassword: form.password } });
      setDone(true);
    } catch (err) {
      setError(err.message || 'Could not reset the password. Please try again.');
    }
  };

  const pwdAdornment = {
    endAdornment: (
      <InputAdornment position="end">
        <IconButton onClick={() => setShowPwd(p => !p)} edge="end">
          {showPwd ? <VisibilityOff /> : <Visibility />}
        </IconButton>
      </InputAdornment>
    ),
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #4a0072 0%, #ae52d4 100%)',
      display: 'flex',
      alignItems: 'center',
    }}>
      <Container maxWidth="xs">
        <Paper elevation={6} sx={{ p: 5, borderRadius: 4 }}>
          <Stack alignItems="center" spacing={1} mb={4}>
            <LockResetIcon sx={{ fontSize: 36, color: 'primary.main' }} />
            <Typography variant="h5" fontWeight={700}>Reset Password</Typography>
            <Typography variant="body2" color="text.secondary">
              Choose a new password for your account
            </Typography>
          </Stack>

          {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

          {!token ? (
            <Alert severity="error">
              This reset link is incomplete. Please use the link from your email or request a new one.
            </Alert>
          ) : done ? (
            <Alert severity="success">
              Your password has been reset. You can now sign in with your new password.
            </Alert>
          ) : (
            <form onSubmit={handleSubmit}>
              <TextField
                label="New Password"
                name="password"
                type={showPwd ? 'text' : 'password'}
                value={form.password}
                onChange={handleChange}
                fullWidth
                required
                autoFocus
                helperText="At least 8 characters"
                sx={{ mb: 3 }}
                InputProps={pwdAdornment}
              />
              <TextField
                label="Confirm New Password"
                name="confirm"
                type={showPwd ? 'text' : 'password'}
                value={form.confirm}
                onChange={handleChange}
                fullWidth
                required
                sx={{ mb: 4 }}
              />
              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                size="large"
                disabled={loading}
                sx={{ fontWeight: 700, py: 1.5 }}
              >
                {loading ? 'Saving...' : 'Reset Password'}
              </Button>
            </form>
          )}

          <Typography variant="body2" align="center" sx={{ mt: 3 }}>
            {token ? (
              <Link component={RouterLink} to="/login" fontWeight={600}>
                {done ? 'Go to Sign In' : 'Back to Sign In'}
              </Link>
            ) : (
              <Link component={RouterLink} to="/forgot-password" fontWeight={600}>
                Request a new link
              </Link>
            )}
          </Typography>
        </Paper>
      </Container>
    </Box>
  );
}
